import { CancelToken } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { createQueriedURL } from '~/Helpers/String.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IRole } from '~/Models/DTO/Role.model';
import { useRequestUser } from '../useRequestUser';

export const useRequestRoles = (cancelToken?: CancelToken) => {
  const { user } = useRequestUser();
  const { isLoading, error, data, refetch, isFetched } = useQuery(
    REQUEST_ACTIONS.USER_ROLES,
    async () =>
      requestReactQuery<IRole[]>({
        url: createQueriedURL(USER_SERVICE_URLS.ROLES, { userId: user?.uuid }),
        config: { method: 'GET', cancelToken },
      }),
    {
      enabled: !!user?.uuid,
      initialData: [],
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
    },
  );

  const {
    data: roleUpdateResponse,
    error: updatingError,
    isLoading: updatingLoading,
    mutate: updateRoles,
  } = useMutation(
    async ({
      userId,
      providerId,
      endDateTime,
    }: {
      userId: string;
      providerId: string;
      endDateTime: string;
    }) =>
      requestReactQuery<IRole>({
        url: USER_SERVICE_URLS.ROLES,
        data: {
          userId,
          providerId,
          endDateTime,
          name: 'FullAccess',
        },
        config: { method: 'PUT' },
      }),
    { mutationKey: REQUEST_ACTIONS.USER_ROLES },
  );

  return {
    roleLoading: isLoading,
    hasRolesLoaded: isFetched,
    roleError: error as IErrorResponse,
    roles: data || [],
    refetchRoles: refetch,
    updatingError: updatingError as IErrorResponse,
    updatingLoading,
    updateRoles,
    roleUpdateResponse,
  };
};
